// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

/* eslint camelcase: 0 */
import { isDemoMode } from "~client-env-utils";

import { CSG, RECIDIVIZ_TENANT } from "../tenants";
import { PartialRecord } from "../utils/typeUtils";
import { DASHBOARD_TENANTS } from "./TenantStore/dashboardTenants";
import * as lantern from "./TenantStore/lanternTenants";
import * as pathways from "./TenantStore/pathwaysTenants";

export type LanternTenants = (typeof lantern.LANTERN_TENANTS)[number];
export type PathwaysTenants = (typeof pathways.PATHWAYS_TENANTS)[number];

export function isPathwaysTenantId(
  tenantId: TenantId | undefined,
): tenantId is PathwaysTenants {
  return pathways.PATHWAYS_TENANTS.includes(tenantId as PathwaysTenants);
}

export function isTenantId(tenantId: string): tenantId is TenantId {
  return TenantIds.includes(tenantId as TenantId);
}

export const TenantIds = DASHBOARD_TENANTS;

export type InternalTenantId = typeof RECIDIVIZ_TENANT | typeof CSG;
export type TenantId = (typeof TenantIds)[number];
export type TenantConfigId = InternalTenantId | TenantId;

export type UserAppMetadata = {
  stateCode: Lowercase<TenantConfigId>;
  allowedStates?: TenantId[];
  allowedSupervisionLocationIds?: string[];
  allowedSupervisionLocationLevel?: string;
  routes?: Record<string, boolean>;
  featureVariants?: FeatureVariantRecord;
  demoModeFeatureVariants?: FeatureVariantRecord;
  userHash?: string;
  pseudonymizedId?: string;
  segmentId?: string;
  intercomId?: string;
  district?: string;
  externalId?: string;
  firstName?: string;
  lastName?: string;
  forceWorkflowsDemoData?: boolean;
};

// TEST is useful for testing, as the name suggests,
// but also so that we don't have an empty union when there are no feature variants in use
export type FeatureVariant =
  | "TEST"
  // WORKFLOWS

  //// General
  | "caseNoteSearch"
  | "classificationScrollBuffer"
  | "clientsResidentsTableViewToggle"
  | "dynamicFilters"
  | "formRevertButton"
  | "hideDenialRevert"
  | "hideWorkflowsOpportunities"
  | "hideWorkflowsResidentsPage"
  | "indefiniteSnooze"
  | "supervisionUnrestrictedSearch"
  | "taskSnoozeReason"
  | "tasksOneRowPerClient"
  | "tasksPermasnooze"
  | "tasksRoutePlanner"
  | "workflowsSupervisorSearch"
  | "typesenseCaseloadSearch"
  | "typesenseHomepage"
  | "typesensePersonSearch"
  | "sentenceProgressV2"
  | "useRecordForIneligibleOpps"
  | "useStableExternalId"

  //// Arizona
  | "usAzAdminSupervisionApprovalFlow"

  //// California
  | "usCaEnableSMS"

  //// Iowa
  | "usIaEarlyDischargeCustomizations"
  | "usIaEarlyDischargeForms"
  | "usIaFinesAndFees"
  | "usIaSupervisionLevelDowngrade"

  //// Idaho
  | "usIdCaseManagerSearch"
  | "usIdCRCFacilitySearch"
  | "usIdDistrictSearch"
  | "usIdTasksV2"
  | "crcLikeBeds"

  //// Michigan
  | "usMiFacilitySearch"
  | "usMiCaseManagerSearch"
  | "usMiCustodyLevelDowngrade"
  | "usMiPositiveOutcomesBanner"
  | "usMiRestrictiveHousingV2"
  | "usMiRestrictiveHousingV2Ineligible"
  | "usMiClassification"
  | "usMiDisplayClassificationTable"

  //// Missouri
  | "usMoOverdueRHPilot"
  | "usMoMyCaseload"
  | "customTasks"
  | "caseOverview"
  | "recentCaseNotes"
  | "usMoCasePlanning"
  | "usMoSarInClientsPage"
  | "usMoSearchByUnit"
  | "usMoWorkRelease"

  //// Oregon
  | "usOrEarnedDischargeSentence"

  //// North Carolina
  | "usNcCrrApprovalFlow"
  | "usNcCrrApprover"
  | "usNcRNAAutoEnablement"

  //// Pennsylvania
  | "usPaSpecialCircumstances"
  | "usPaUnclearEligibility"

  //// Tennessee
  | "usTnCompliantReportingWriteback"
  | "usTnDoNotMarkPendingOnDownload"
  | "usTnExpiration"
  | "usTnExpirationSubmitToTomis"
  | "usTnInitialClassification"
  | "usTnSuspensionOfDirectSupervision"
  | "usTnTEPENotesForAll"
  | "usTn2026ClassificationPolicyPilot"
  | "usTnRcafV2"
  | "usTnRcafV1"
  | "usTnCafSubmissionButton"
  | "trusteeChecklistRework"
  | "clientProfileWarmHandoff"

  //// Texas
  | "usTxAnnualReportStatusV1"
  | "usTxEarlyReleaseFromSupervisionV1"
  | "usTxAnnualReportStatusV2"
  | "usTxEarlyReleaseFromSupervisionV2"
  | "usTxEnablePoFormSection"
  | "usTxEnableSupervisorFormSection"
  | "usTxAllowSingleFormSectionEdit"

  // INSIGHTS
  | "insightsLeadershipPageAllDistricts"
  | "insightsOnboarding"
  | "supervisorHomepageWorkflows"
  | "supervisorHomepageVitals"
  | "actionStrategies"
  | "zeroGrantsFlag"
  | "outcomesModule"
  | "reportIncorrectRosters"
  | "operationsDrilldown"
  | "operationsContactsDrilldown"
  | "operationsNumeratorDenominatorIsEnabled"
  | "insightsStaffUsage"
  | "insightsConsistentLoginPill"
  | "insightsHideOfficersWithoutEligibleCaseload"
  | "supervisorHomepageReviewCard"
  | "insightsSupervisorOpportunityNotifications"
  | "supervisorHomepageReviewTable"
  | "logInToOwnSupervisorPage"
  | "showPreviouslyReviewedOpportunities"

  // SENTENCING
  | "offenseOverrideControls"
  | "protectiveFactors"
  | "editCountyFields"
  | "mandatoryMinimum"
  | "snoozeCompanions"
  | "splitParoleProbationOutcomes"
  | "SARBuilder"
  | "SARSignature"
  | "SARManualORAS"
  | "reactPdfSAR"
  | "SARImportEmploymentRecords"
  | "SARSexualHistory"
  | "PSRBuilder"

  // HOME CONTACT ROUTE PLANNER
  | "HCRPAddMoreClients"
  | "HCRPPIIEmail";

export type FeatureVariantValue = {
  activeDate?: Date;
  variant?: string;
  activeTenants?: TenantId[];
};
/**
 * For each feature, an optional activeDate can control when the user gets access.
 * If this is missing, access will be granted immediately.
 * The `variant` property can be used to segment users to different variants of the feature,
 * e.g. for A/B testing.
 */
export type FeatureVariantMapping = Record<FeatureVariant, FeatureVariantValue>;
export type FeatureVariantRecord = Partial<FeatureVariantMapping>;
export type ActiveFeatureVariantRecord = PartialRecord<
  FeatureVariant,
  Omit<FeatureVariantValue, "activeDate" | "activeTenants">
>;
export type FeatureVariantOverrideRecord = PartialRecord<
  FeatureVariant,
  boolean
>;
export const allFeatureVariants: FeatureVariantMapping = {
  TEST: {},
  usAzAdminSupervisionApprovalFlow: {},
  usTnExpiration: {},
  usTnExpirationSubmitToTomis: {},
  usCaEnableSMS: {},
  supervisionUnrestrictedSearch: {},
  formRevertButton: {},
  usMoOverdueRHPilot: {},
  insightsLeadershipPageAllDistricts: {},
  insightsOnboarding: {},
  hideDenialRevert: {},
  workflowsSupervisorSearch: {},
  typesenseCaseloadSearch: {},
  typesenseHomepage: {},
  typesensePersonSearch: {},
  supervisorHomepageWorkflows: {},
  supervisorHomepageVitals: {},
  usPaSpecialCircumstances: {},
  caseNoteSearch: {},
  classificationScrollBuffer: {},
  clientsResidentsTableViewToggle: {},
  dynamicFilters: {},
  actionStrategies: {},
  zeroGrantsFlag: {},
  usOrEarnedDischargeSentence: {},
  offenseOverrideControls: {},
  protectiveFactors: {},
  editCountyFields: {},
  usIdCaseManagerSearch: {},
  usIdCRCFacilitySearch: {},
  usTnSuspensionOfDirectSupervision: {},
  outcomesModule: {},
  mandatoryMinimum: {},
  usTnCompliantReportingWriteback: {},
  usTnDoNotMarkPendingOnDownload: {},
  reportIncorrectRosters: {},
  snoozeCompanions: {},
  usIaEarlyDischargeCustomizations: {},
  usIaEarlyDischargeForms: {},
  usIaSupervisionLevelDowngrade: {},
  operationsDrilldown: {},
  operationsContactsDrilldown: {},
  operationsNumeratorDenominatorIsEnabled: {},
  usTnInitialClassification: {},
  usTn2026ClassificationPolicyPilot: {},
  usTnRcafV2: {},
  usTnRcafV1: {},
  usTnCafSubmissionButton: {},
  trusteeChecklistRework: {},
  clientProfileWarmHandoff: {},
  insightsStaffUsage: {},
  insightsConsistentLoginPill: {},
  insightsHideOfficersWithoutEligibleCaseload: {},
  usTnTEPENotesForAll: {},
  usMoMyCaseload: {},
  caseOverview: {},
  recentCaseNotes: {},
  usMoCasePlanning: {},
  usMoSarInClientsPage: {},
  usMoSearchByUnit: {},
  usMoWorkRelease: {},
  usMiCaseManagerSearch: {},
  usMiCustodyLevelDowngrade: {},
  usMiPositiveOutcomesBanner: {},
  usMiFacilitySearch: {},
  usMiRestrictiveHousingV2: {},
  usMiRestrictiveHousingV2Ineligible: {},
  usMiClassification: {},
  usMiDisplayClassificationTable: {},
  usNcCrrApprovalFlow: {},
  usNcCrrApprover: {},
  usNcRNAAutoEnablement: {},
  indefiniteSnooze: {},
  usIdDistrictSearch: {},
  usIaFinesAndFees: {},
  customTasks: {},
  taskSnoozeReason: {},
  tasksOneRowPerClient: {},
  tasksPermasnooze: {},
  tasksRoutePlanner: {},
  HCRPAddMoreClients: {},
  HCRPPIIEmail: {},
  usPaUnclearEligibility: {},
  usIdTasksV2: {},
  crcLikeBeds: {},
  SARBuilder: {},
  SARSignature: {},
  SARManualORAS: {},
  reactPdfSAR: {},
  SARImportEmploymentRecords: {},
  SARSexualHistory: {},
  PSRBuilder: {},
  splitParoleProbationOutcomes: {},
  supervisorHomepageReviewCard: {},
  insightsSupervisorOpportunityNotifications: {},
  supervisorHomepageReviewTable: {},
  logInToOwnSupervisorPage: {},
  showPreviouslyReviewedOpportunities: {},
  usTxAnnualReportStatusV1: {},
  usTxEarlyReleaseFromSupervisionV1: {},
  usTxAnnualReportStatusV2: {},
  usTxEarlyReleaseFromSupervisionV2: {},
  usTxEnablePoFormSection: {},
  usTxEnableSupervisorFormSection: {},
  usTxAllowSingleFormSectionEdit: {},
  sentenceProgressV2: {},
  hideWorkflowsOpportunities: {},
  hideWorkflowsResidentsPage: {},
  useRecordForIneligibleOpps: {},
  useStableExternalId: {},
};
export const defaultRecidivizUserFeatureVariantsActive: Partial<FeatureVariantMapping> =
  import.meta.env.VITE_DEPLOY_ENV === "production"
    ? {
        actionStrategies: { activeTenants: ["US_MI"] },
        crcLikeBeds: {},
        insightsOnboarding: {},
        outcomesModule: {
          activeTenants: ["US_AZ", "US_CA", "US_ID", "US_MI", "US_TN"],
        },
        supervisionUnrestrictedSearch: {},
        supervisorHomepageReviewCard: {
          activeTenants: ["US_AZ", "US_IA", "US_MI", "US_TX"],
        },
        showPreviouslyReviewedOpportunities: {
          activeTenants: ["US_TX"],
        },
        usTxAnnualReportStatusV2: { activeTenants: ["US_TX"] },
        usTxEarlyReleaseFromSupervisionV2: { activeTenants: ["US_TX"] },
        supervisorHomepageReviewTable: { activeTenants: ["US_TX"] },
        supervisorHomepageWorkflows: {},
        supervisorHomepageVitals: {
          activeTenants: ["US_ID", "US_ND", "US_TX"],
        },
        operationsDrilldown: { activeTenants: ["US_ID", "US_ND", "US_TX"] },
        operationsContactsDrilldown: { activeTenants: ["US_TX"] },
        operationsNumeratorDenominatorIsEnabled: { activeTenants: ["US_TX"] },
        insightsStaffUsage: {
          activeTenants: ["US_ID", "US_UT", "US_IA", "US_MI", "US_TX"],
        },
        insightsConsistentLoginPill: { activeTenants: ["US_MI"] },
        insightsHideOfficersWithoutEligibleCaseload: {
          activeTenants: ["US_MI"],
        },
        clientsResidentsTableViewToggle: {},
        usIdCaseManagerSearch: {},
        usIdCRCFacilitySearch: {},
        usMiCaseManagerSearch: {},
        usMiCustodyLevelDowngrade: {},
        usMiRestrictiveHousingV2Ineligible: { activeTenants: ["US_MI"] },
        usMiClassification: {},
        usMiDisplayClassificationTable: undefined,
        usMiFacilitySearch: {},
        usMiRestrictiveHousingV2: {},
        usMoOverdueRHPilot: {},
        usOrEarnedDischargeSentence: {},
        usTnExpiration: {},
        usTnExpirationSubmitToTomis: {},
        usTnSuspensionOfDirectSupervision: {},
        usTxEnablePoFormSection: { activeTenants: ["US_TX"] },
        usTxEnableSupervisorFormSection: { activeTenants: ["US_TX"] },
        zeroGrantsFlag: { activeTenants: ["US_ID", "US_MI", "US_TN", "US_PA"] },
        usTnInitialClassification: {},
        usTnTEPENotesForAll: {},
        usMoSarInClientsPage: { activeTenants: ["US_MO"] },
        usMoSearchByUnit: {},
        usMoWorkRelease: {},
        indefiniteSnooze: { activeTenants: ["US_IA"] },
        useStableExternalId: { activeTenants: ["US_TX"] },
        usIaEarlyDischargeForms: {},
        usIaFinesAndFees: {},
        usIaSupervisionLevelDowngrade: {},
        hideDenialRevert: { activeTenants: ["US_AZ"] },
        usIdTasksV2: {},
        caseOverview: { activeTenants: ["US_MO"] },
        dynamicFilters: { activeTenants: ["US_MO"] },
        recentCaseNotes: { activeTenants: ["US_MO"] },
        usMoCasePlanning: { activeTenants: ["US_MO"] },
        customTasks: { activeTenants: ["US_MO"] },
        tasksOneRowPerClient: { activeTenants: ["US_MO"] },
        tasksRoutePlanner: { activeTenants: ["US_TX", "US_ID"] },
        HCRPAddMoreClients: { activeTenants: ["US_TX", "US_ID"] },
        HCRPPIIEmail: { activeTenants: ["US_ID"] },
        hideWorkflowsResidentsPage: { activeTenants: ["US_NC"] },
        sentenceProgressV2: { activeTenants: ["US_ND"] },
      }
    : {
        ...allFeatureVariants,
        // Off for Recidiviz users during phase 1 build-out — opt in per-user
        // via the admin panel to actually see the new Typesense search bar.
        typesenseCaseloadSearch: undefined,
        // Off during phase 2 build-out.
        typesenseHomepage: undefined,
        // Off for Recidiviz users during phase 1 build-out — opt in per-user
        // via the admin panel to actually see the new Typesense person search.
        typesensePersonSearch: undefined,
        usOrEarnedDischargeSentence: undefined,
        clientProfileWarmHandoff: undefined,
        outcomesModule: {
          activeTenants: ["US_AZ", "US_CA", "US_ID", "US_MI", "US_TN"],
        },
        // TODO(recidiviz-data/#75828): Remove once we support 2026 versions in demo mode
        usTn2026ClassificationPolicyPilot: isDemoMode() ? undefined : {},
        usTnRcafV1: undefined,
        usTnRcafV2: isDemoMode() ? undefined : {},
        usTnCafSubmissionButton: undefined,
        // Off for Recidiviz users during build-out — opt in per-user
        // via the admin panel to see the reworked Trustee checklist.
        trusteeChecklistRework: undefined,

        operationsDrilldown: { activeTenants: ["US_ID", "US_ND", "US_TX"] },
        operationsContactsDrilldown: { activeTenants: ["US_TX"] },
        operationsNumeratorDenominatorIsEnabled: { activeTenants: ["US_TX"] },
        useStableExternalId: { activeTenants: ["US_TX"] },
        reportIncorrectRosters: {
          activeTenants: ["US_TN"],
        },
        caseOverview: { activeTenants: ["US_MO"] },
        dynamicFilters: { activeTenants: ["US_MO"] },
        recentCaseNotes: { activeTenants: ["US_MO"] },
        usMoCasePlanning: { activeTenants: ["US_MO"] },
        tasksPermasnooze: { activeTenants: ["US_MO"] },
        usMoMyCaseload: { activeTenants: ["US_MO"] },
        usMoSarInClientsPage: { activeTenants: ["US_MO"] },
        splitParoleProbationOutcomes: { activeTenants: ["US_MI"] },
        customTasks: { activeTenants: ["US_MO"] },
        tasksOneRowPerClient: { activeTenants: ["US_MO"] },
        tasksRoutePlanner: { activeTenants: ["US_TX", "US_ID"] },
        HCRPAddMoreClients: { activeTenants: ["US_TX", "US_ID"] },
        HCRPPIIEmail: { activeTenants: ["US_ID"] },
        hideDenialRevert: { activeTenants: ["US_AZ"] },
        SARBuilder: { activeTenants: ["US_MO"] },
        SARSignature: { activeTenants: ["US_MO"] },
        SARManualORAS: { activeTenants: ["US_MO"] },
        SARImportEmploymentRecords: { activeTenants: ["US_MO"] },
        PSRBuilder: { activeTenants: ["US_MO"] },
        SARSexualHistory: { activeTenants: ["US_MO"] },
        sentenceProgressV2: { activeTenants: ["US_ND"] },
        hideWorkflowsOpportunities: undefined,
        hideWorkflowsResidentsPage: { activeTenants: ["US_NC"] },
        supervisorHomepageReviewCard: {
          activeTenants: ["US_AZ", "US_IA", "US_MI", "US_TX"],
        },
        supervisorHomepageReviewTable: { activeTenants: ["US_TX"] },
        showPreviouslyReviewedOpportunities: {
          activeTenants: ["US_TX"],
        },
        usTxEnablePoFormSection: { activeTenants: ["US_TX"] },
        usTxEnableSupervisorFormSection: { activeTenants: ["US_TX"] },
        usMiDisplayClassificationTable: undefined,
      };

export type LanternMethodologyByTenant = {
  [key in LanternTenants]: LanternMethodology;
};

export type LanternMethodology = {
  [k: string]: {
    id: number;
    title?: string;
    methodology: string;
  }[];
};
